import React, {
	useImperativeHandle,
	useLayoutEffect,
	useEffect,
	useRef,
	useState,
} from "react";
import { gsap } from "gsap";

function Slider(props) {
	const sliderMoverRef = useRef(null);
	const [curValue, setCurValue] = useState(0);
	useEffect(() => {
		gsap.killTweensOf(sliderMoverRef.current);
		gsap.to(sliderMoverRef.current, {
			ease: "none",
			duration: 1,
			xPercent: (props.value || 0) * 100,
			onUpdate: () => {
				setCurValue(
					gsap.getProperty(sliderMoverRef.current, "xPercent")
				);
			},
		});
	}, [props.value]);

	return (
		<div className="status-slider">
			<div className="complications-slider-mover" ref={sliderMoverRef}>
				<div className="complications-slider-indicator" />
			</div>
			<div className="complications-slider-txt">
				{(curValue / 100).toFixed(3)}
			</div>
		</div>
	);
}

function LedBar(props) {
	const ledContainer = useRef(null);
	const [numberOfLeds] = useState(props.lednumber || 19);
	const [ledNodes, setLedNodes] = useState(null);

	const switchLedStatus = (led, status, on) => {
		let statusColor = "#fffcfa";

		switch (status) {
			case "green":
				statusColor = "#68CE67";
				break;
			case "yellow":
				statusColor = "#F2A33C";
				break;
		}
		gsap.killTweensOf(led);
		gsap.to(led, {
			duration: 0.15,
			ease: "none",
			backgroundColor: on ? statusColor : "#606067",
		});
	};

	const refreshStatus = () => {
		const onLedNumber = ledNodes.length * props.value;
		ledNodes.forEach((x, i) => {
			switchLedStatus(
				x,
				props.value > 0.2 ? "green" : "yellow",
				i <= onLedNumber
			);
		});
	};
	useEffect(() => {
		if (ledNodes && props.value) refreshStatus();
	}, [ledNodes, props.value]);

	useLayoutEffect(() => {
		setLedNodes(ledContainer.current.querySelectorAll(".status-led"));
	}, [props.lednumber]);
	return (
		<div className="compilation-row">
			<div className="complication-title start">
				{props.title || "N/A"}
			</div>
			<div className="complications-status-cnt" ref={ledContainer}>
				{[...Array(numberOfLeds)].map((x, i) => {
					const middle = 5;

					return (
						<div
							key={"status_led_" + i}
							className={`status-led${
								middle == i ? " full" : ""
							}`}
						/>
					);
				})}
			</div>
		</div>
	);
}

const CompStatusSlider = React.forwardRef((props, ref) => {
	const [values, setValues] = useState(null);
	const animationTimeline = useRef(null);

	const complicationsRef = useRef(null);

	const seek = (e) => {
		const seekTime = e?.detail?.playSecond;

		if (seekTime) {
			animationTimeline.current?.seek?.(seekTime);
			animationTimeline.current?.play?.();
		}
	};
	const restart = () => {
		animationTimeline.current?.restart?.();
	};

	const pause = () => {
		animationTimeline.current?.pause?.();
	};
	useLayoutEffect(() => {
		const parent = complicationsRef.current?.parentNode || null;

		if (parent) {
			parent?.addEventListener("onRestart", restart);
			parent?.addEventListener("onSeek", seek);
			parent?.addEventListener("onPause", pause);
		}
		return () => {
			parent?.removeEventListener("onRestart", restart);
			parent?.removeEventListener("onSeek", seek);
			parent?.removeEventListener("onPause", pause);
		};
	}, [complicationsRef.current]);

	const updateValues = (newValues) => {
		setValues(newValues);
	};

	const reframe = () => {
		gsap.set(complicationsRef.current, {
			x: props.offsetX || 0,
			scale: props.isSmallScreen ? 0.75 : 1,
		});
	};

	useEffect(() => {
		reframe();
	}, [props.offsetX, props.isSmallScreen]);

	useLayoutEffect(() => {
		if (props.duration && props.data) {
			const firstEntry = props.data[0];
			const lastEntry = props.data?.[props.data?.length - 1];

			if (firstEntry && lastEntry) {
				const duration = lastEntry.Time - firstEntry?.Time;
				const timefactor = props.duration / duration;
				animationTimeline.current = gsap.timeline({ repeat: -1 });

				props.data.forEach((timeentry, i) => {
					const previousTime = props.data?.[i - 1]?.Time || 0;

					const delay = (timeentry.Time - previousTime) * timefactor;

					animationTimeline.current.add(
						gsap.delayedCall(delay, updateValues, [timeentry])
					);
				});
			}
		}

		return () => {
			if (animationTimeline.current) {
				animationTimeline.current.getChildren().forEach((x) => {
					x.kill();
				});
				animationTimeline.current = null;
			}
		};
	}, [props.data, props.duration]);

	return (
		<div
			className={`complication-el connection${
				props.transparent ? " transparent" : ""
			}${props.align ? " align-" + props.align : ""}`}
			ref={complicationsRef}
		>
			<div className="complication-title-cnt">
				<div className="complication-logo" />
				<div className="complication-title green">
					{" "}
					{props.title || "N/A"}
				</div>
			</div>

			{Object.keys(props.data?.[0] || []).map((x, i) => {
				if (x == "Time") return null;
				return (
					<LedBar
						key={"comp-led-node_" + i}
						title={x}
						value={Object.values(values || {})?.[i] || 0}
					/>
				);
			})}
		</div>
	);
});

export default CompStatusSlider;
